import React from 'react';

import { cn } from '@/lib/utils';
import { useFormatPrice } from '../../../common/hooks/use-format-price.hook';
import type { PriceUnit } from '../../../common/utils/product-pricing';

export interface PriceTextProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
    price: { amount: number; currency: string; unit?: PriceUnit | null; compareAt?: number } | null | undefined;
    /** Leave out "/ m²". */
    hideUnit?: boolean;
}

/**
 * A price as a shopper would read it — "€45.90 / m²" — with the struck-through
 * compare-at price in front when there is one. Nothing at all when there is no
 * price: a missing price is never shown as zero.
 */
export const PriceText = ({ price, hideUnit, className, ...rest }: PriceTextProps) => {
    const format = useFormatPrice();
    if (!price || !(price.amount > 0) || !price.currency) return null;

    return (
        <span className={cn('tabular-nums', className)} {...rest}>
            {price.compareAt && price.compareAt > price.amount ? (
                <s className="me-1.5 text-muted-foreground">{format.amount(price.compareAt, price.currency)}</s>
            ) : null}
            {format.price(hideUnit ? { ...price, unit: undefined } : price)}
        </span>
    );
};
